import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import config from '@/config'
import { structure, breakpoints, colors } from '@/styles'
import LandingBG from './landing-bg.png'

const { APP_PATH, WEB_URL } = config

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url('${LandingBG}');
  background-repeat: no-repeat;
  background-position: bottom center;
  background-size: cover;
  min-height: 800px;
  height: 100vh;
  position: relative;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    height: unset;
    min-height: calc(100vh - ${structure.footer.height}px);
    align-items: flex-start;
    background-image: unset;
    background-color: ${props => (props.blue ? '#e9f2fc' : colors.grey96)};

    @supports (min-height: 100svh) {
      min-height: calc(100svh - ${structure.footer.height}px);
    }
  }
`

const Callback = ({ authToken, url }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const [mounted, setMounted] = useState(false)

  const openEndTenancyModalInChat = {
    string: '[Opens End Tenancy Modal in Chat]',
    action: options => {
      const state = { openEndTenancyModalFromApp: true, listing_id: options.listingId }
      history.replace(`${APP_PATH}/chat/${options.chatRoomId}`, state)
    }
  }

  const mapping = {
    chats: {
      chat: {
        string: ''
      },
      chat_all_signed_contract: {
        string: `/contract/{contract_id}`,
        action: options => {
          return window.open(
            `${APP_PATH}/contract/${options.contractId}`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      chat_appointment_canceled: { string: '' },
      chat_appointment_user_subscribed: {
        string: '/app/listing/{listing_id}/showings',
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return isTenant
            ? window.location.replace('/')
            : window.open(
                `${APP_PATH}/listing/${options.listingId}/showings`,
                '_self',
                'noopener,noreferrer'
              )
        }
      },
      chat_appointment_user_unsubscribed: { string: '' },
      chat_cancel_application: { string: '' },
      chat_cancel_contract: { string: '' },
      chat_end_contract_ended: { string: '' },
      chat_end_contract_moving_out: { string: '' },
      chat_end_contract_renew_refused: { string: '' },
      chat_end_contract_renew_requested: {
        string:
          'landlord: /app/listing/{listing_id}/contract/{contract_id} renter: /app/rental/6468/contract/2221 (Chat event card same as {chat_new_contract})',
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return window.open(
            isTenant
              ? `${APP_PATH}/rental/${options.listingId}/contract/${options.contractId}`
              : `${APP_PATH}/listing/${options.listingId}/contract/${options.contractId}`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      chat_end_tenancy_request_done: openEndTenancyModalInChat,
      chat_end_tenancy_request_cancelled: openEndTenancyModalInChat,
      chat_end_tenancy_request_confirmed: openEndTenancyModalInChat,
      chat_end_tenancy_request_created: openEndTenancyModalInChat,
      chat_end_tenancy_request_refused: openEndTenancyModalInChat,
      chat_end_tenancy_support_requested: openEndTenancyModalInChat,
      chat_listing_has_been_rented: { string: 'External Rental Added' },
      chat_new_contract: {
        string: '/app/contract/{contract_id}',
        action: options => {
          return window.open(
            `${APP_PATH}/contract/${options.contractId}`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      chat_id_verification_request: { string: '' },
      chat_listing_verification_request: { string: '(No chat event card -> bug?)' },
      chat_new_application: {
        string: '',
        action: options => {
          return window.open(
            `${APP_PATH}/chat/${options.chatRoomId}/application/${options.applicationId}`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      chat_payment_request_approved: {
        string: '',
        action: options => {
          return window.open(
            `${APP_PATH}/tenant/${options.listingId}/payments/${options.paymentId}`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      chat_payment_request_cancelled: { string: '' },
      chat_payment_request_created: {
        string: '[Opens Payment Request Modal in Chat]',
        action: options => {
          const state = {
            openPaymentRequestModalFromApp: true,
            listing_id: options.listingId,
            payment_id: options.paymentId
          }
          history.replace(`/chat/${options.chatRoomId}`, state)
        }
      },
      chat_user_signed_contract: {
        string: '/app/contract/{contract_id}',
        action: options => {
          return window.open(
            `${APP_PATH}/contract/${options.contractId}`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      chat_contract_extension_started: { string: '' },
      chat_payment_transaction_unresolved_underpaid: { string: '' },
      chat_custom_contracts_ready_review_sign: {
        string: '/app/custom-contract/sign/{contract_id}/1',
        action: options => {
          return window.open(
            `${APP_PATH}/custom-contract/sign/${options.contractId}/1`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      chat_custom_contracts_void_all: { string: '' },
      chat_custom_contracts_void_single: { string: '' },
      chat_custom_contracts_document_complete: {
        string: '/app/contract/manager/{contract_id}',
        action: options => {
          return window.open(
            `${APP_PATH}/contract/manager/${options.contractId}`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      chat_timeline_deleted: { string: '' },
      chat_file_img: { string: '[Opens image in lightbox]' },
      chat_file_secure: {
        string:
          'file:///Users/{user_id}/Downloads/Document%{document_id}.pdf (ex. file:///Users/kynarollo/Downloads/Document%203.pdf)'
      },
      chat_application_invite: {
        string: "renter: /application/create/{listing_id} landlord: ''",
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return isTenant
            ? window.open(
                `${APP_PATH}/application/create/${options.listingId}`,
                '_self',
                'noopener,noreferrer'
              )
            : window.location.replace('/')
        }
      },
      chat_first_message: { string: '' },
      chat_initial_message: { string: '' },
      chat_new_user: { string: '' },
      chat_remove_user: { string: '' },
      chat_timeline_locked: { string: '' },
      phantom_message: { string: '' },
      chat_listing_match: { string: '' },
      chat_welcome_message: { string: '' },
      chat_copy_application_from: { string: '' },
      chat_copy_application_to: { string: '' },
      chat_welcome_support: { string: '' },
      chat_end_contract_notice1: { string: '' },
      chat_end_contract_notice2: { string: '' },
      chat_view_listing: {
        string: '',
        action: options => {
          const isTenant = options.userRole === 'tenant'
          const isRented = options.state_machine === 'rented'
          return isTenant
            ? window.open(`${APP_PATH}/rental/${options.listingId}`, '_self', 'noopener,noreferrer')
            : isRented
            ? window.open(`${APP_PATH}/tenant/${options.listingId}`, '_self', 'noopener,noreferrer')
            : window.open(
                `${APP_PATH}/listing/${options.listingId}`,
                '_self',
                'noopener,noreferrer'
              )
        }
      }
    },
    supports: {
      support_room_event_chat: { string: '' },
      support_room_internal_note: { string: '' },
      support_room_event_chat_bot_file_img: { string: '' },
      support_room_event_chat_bot_file_secure: { string: '' }
    },
    listings: {
      listing_document_approval_accepted: { string: '' },
      listing_document_approval_declined: { string: '' }
    },
    contracts: {},
    settings: {
      profile: {
        string: '',
        action: options => {
          return window.open(`${APP_PATH}/profile`, '_self', 'noopener,noreferrer')
        }
      },
      payment_settings: {
        string: '',
        action: options => {
          return window.open(
            `${APP_PATH}/settings/payment-settings`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      billding_address: {
        string: '',
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return window.open(
            isTenant
              ? `${APP_PATH}/settings/payment-settings/billing-address`
              : `${APP_PATH}/settings/payment-settings`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      manage_users: {
        string: '',
        action: options => {
          return window.open(`${APP_PATH}/manage/users`, '_self', 'noopener,noreferrer')
        }
      },
      manage_listings: {
        string: '',
        action: options => {
          return window.open(`${APP_PATH}/manage/listings`, '_self', 'noopener,noreferrer')
        }
      },
      verify_user: {
        string: '',
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return window.open(
            isTenant
              ? `${APP_PATH}/profile/verify`
              : `${APP_PATH}/profile?openVerifyUserModal=true`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      verify_business_licence: {
        string: '',
        action: () => {
          return window.open(
            `${APP_PATH}/profile?openVerifyBusinessLicenceModal=true`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      livScore: {
        string: '',
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return window.open(
            isTenant ? `${APP_PATH}/profile/trust-score` : `${APP_PATH}/profile`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      reference: {
        string: '',
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return window.open(
            isTenant ? `${APP_PATH}/profile/references` : `${APP_PATH}/profile`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      verifyId: {
        string: '',
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return window.open(
            isTenant ? `${APP_PATH}/profile/trust-score` : `${APP_PATH}/profile`,
            '_self',
            'noopener,noreferrer'
          )
        }
      }
    },
    applications: {
      create_application: {
        string: "renter: /application/create/{listing_id} landlord: ''",
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return isTenant
            ? window.open(
                `${APP_PATH}/application/create/${options.listingId}`,
                '_self',
                'noopener,noreferrer'
              )
            : window.location.replace('/')
        }
      },
      view_application: {
        string: '',
        action: options => {
          return window.open(
            `${APP_PATH}/application/${options.applicationId}`,
            '_self',
            'noopener,noreferrer'
          )
        }
      }
    },
    payments: {
      payment_method_pad: {
        string:
          'renter: /app/rental/{listing_id}/payments/(contract_id)/pre-authorized-debit -> landlord: /app/tenant/{listing_id}/payments/(contract_id)/pre-authorized-debit',
        action: options => {
          const isTenant = options.userRole === 'tenant'
          return window.open(
            isTenant
              ? `${APP_PATH}/rental/${options.listingId}/payments/${options.contractId}/pre-authorized-debit`
              : `${APP_PATH}/tenant/${options.listingId}/payments/${options.contractId}/pre-authorized-debit`,
            '_self',
            'noopener,noreferrer'
          )
        }
      },
      payment_method_other: { string: '' },
      pad_failed: { string: '' }
    },
    landing: {
      kratos: {
        string: '',
        action: () => {
          return window.open(`${WEB_URL}`, '_self', 'noopener,noreferrer')
        }
      }
    }
  }

  const redirect = () => {
    window.location.replace('/')
  }

  const fetchData = async () => {
    const authAndRedirect = async authToken => {
      const { tokenSessionBody } = await dispatch.auth.getSessionWithToken(authToken)

      const { target, options } = tokenSessionBody
      const { eventType } = options

      const action = mapping[target][eventType].action || null

      if (action) {
        action(options)
      } else {
        redirect()
      }
    }

    // Brevo redirect
    if (url) {
      const { tokenSessionResponse } = await dispatch.auth.getSessionWithToken(authToken)

      if (tokenSessionResponse.ok) {
        window.open(`${APP_PATH}/${url}`, '_self', 'noopener,noreferrer')
      }
      return
    }

    if (authToken) {
      const { response } = await dispatch.auth.tokenSessionValidate(authToken)

      if (!response.ok) {
        if (response.status === 400) redirect()
        await dispatch.auth.logout()
        await authAndRedirect(authToken)
      } else {
        await authAndRedirect(authToken)
      }
    } else {
      redirect()
    }
  }

  if (!mounted) {
    setMounted(true)
    fetchData()
  }
  return (
    <Container blue>
      <Helmet>
        <title>{t('b.landing.view.callback.title', 'Callback')}</title>
        <link rel="canonical" href="https://liv.rent/app" />
      </Helmet>
    </Container>
  )
}

export default Callback
